import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { usePageTitle } from '../../context/PageTitleContext';
import { useSidebar } from '../../context/SidebarContext';
import { advancedReportService } from '../../services/api';
import { Card, Table, PageHeader, Pagination } from '../common';
import { default as usePageState } from '../../hooks/usePageState';
import { downloadExcel } from '../../utils/exportUtils';
import { useAuth } from '../../context/AuthContext';

const SlaReport = () => {
  const { setPageTitle, setPageSubtitle } = usePageTitle();
  const { isCollapsed } = useSidebar();
  const { getPagePermissions } = useAuth();
  const permissions = getPagePermissions('/reports/sla');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({});
  const [filterOptions, setFilterOptions] = useState({ regions: [], priorities: [] });

  const [filters, setFilters] = usePageState('slaReport', {
    start_date: '',
    end_date: '',
    region_code: '',
    priority: '',
    currentPage: 1,
    itemsPerPage: 30,
  }, ['/reports']);

  useEffect(() => {
    setPageTitle('SLA Report');
    setPageSubtitle('SLA compliance analysis by priority');
    fetchFilterOptions();
  }, [setPageTitle, setPageSubtitle]);

  const fetchFilterOptions = async () => {
    try {
      const response = await advancedReportService.getFilters();
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.start_date) params.filter = 'custom';
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.region_code) params.region_code = filters.region_code;
      if (filters.priority) params.priority = filters.priority;
      const response = await advancedReportService.getSlaReport(params);
      setData(response.data?.data || []);
      setTotals(response.data?.totals || {});
    } catch (error) {
      console.error('Error fetching SLA report:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.start_date, filters.end_date, filters.region_code, filters.priority]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatHours = (hours) => {
    if (!hours || hours === 0) return '-';
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    return `${hours}h`;
  };

  const getPriorityBadge = (priority) => {
    const colorMap = {
      'High': 'bg-red-100 text-red-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'Low': 'bg-green-100 text-green-800',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${colorMap[priority] || 'bg-gray-100 text-gray-800'}`}>
        {priority}
      </span>
    );
  };

  // Pagination
  const totalItems = data.length;
  const totalPages = Math.ceil(totalItems / filters.itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (filters.currentPage - 1) * filters.itemsPerPage;
    return data.slice(start, start + filters.itemsPerPage);
  }, [data, filters.currentPage, filters.itemsPerPage]);

  const handleDownload = () => {
    downloadExcel(data, columns, 'sla_report');
  };

  const columns = [
    {
      header: 'Priority',
      accessor: 'priority',
      sticky: true,
      render: (value) => getPriorityBadge(value)
    },
    {
      header: 'Total Tickets',
      accessor: 'totalTickets',
      align: 'center',
      render: (value) => <span className="font-semibold">{value}</span>
    },
    {
      header: 'Resolved',
      accessor: 'totalResolved',
      align: 'center',
      render: (value) => <span className="font-semibold text-green-600">{value}</span>
    },
    {
      header: 'Breached',
      accessor: 'breached',
      align: 'center',
      render: (value) => (
        <span className={`font-semibold ${value > 0 ? 'text-red-600' : 'text-green-600'}`}>{value}</span>
      )
    },
    {
      header: 'SLA %',
      accessor: 'slaPct',
      align: 'center',
      render: (value) => (
        <div className="flex items-center justify-center gap-2">
          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[60px]">
            <div
              className="h-2 rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(value, 100)}%`,
                backgroundColor: value >= 80 ? '#10b981' : value >= 50 ? '#f59e0b' : '#ef4444'
              }}
            />
          </div>
          <span className="font-medium text-sm" style={{ color: value >= 80 ? '#059669' : value >= 50 ? '#d97706' : '#dc2626' }}>
            {value}%
          </span>
        </div>
      )
    },
    {
      header: 'Avg Actual Time',
      accessor: 'avgActualTime',
      align: 'center',
      render: (value) => <span className="text-gray-700 font-medium">{formatHours(value)}</span>
    },
    {
      header: 'SLA Expected Time',
      accessor: 'avgSlaExpectedTime',
      align: 'center',
      render: (value) => <span className="text-gray-700 font-medium">{formatHours(value)}</span>
    },
    {
      header: 'Avg Delay',
      accessor: 'avgDelayTime',
      align: 'center',
      render: (value) => (
        <span className={`font-medium ${value > 0 ? 'text-red-600' : 'text-gray-500'}`}>
          {value > 0 ? formatHours(value) : '-'}
        </span>
      )
    },
    {
      header: 'Near Breach',
      accessor: 'nearBreachCount',
      align: 'center',
      render: (value) => (
        <span className={`font-semibold ${value > 0 ? 'text-orange-600' : 'text-gray-500'}`}>{value}</span>
      )
    },
    {
      header: 'Near Breach Solved',
      accessor: 'nearBreachSolved',
      align: 'center',
      render: (value) => (
        <span className={`font-semibold ${value > 0 ? 'text-blue-600' : 'text-gray-500'}`}>{value}</span>
      )
    },
  ];

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader
          filters={{
            dateRange: {
              startDate: filters.start_date,
              endDate: filters.end_date,
              onStartDateChange: (e) => setFilters({ ...filters, start_date: e.target.value, currentPage: 1 }),
              onEndDateChange: (e) => setFilters({ ...filters, end_date: e.target.value, currentPage: 1 }),
            },
            dropdowns: [
              {
                value: filters.region_code,
                onChange: (e) => setFilters({ ...filters, region_code: e.target.value, currentPage: 1 }),
                options: [{ value: '', label: 'All Regions' }, ...(filterOptions.regions || [])],
                placeholder: 'All Regions',
                searchable: true,
                width: '180px'
              },
              {
                value: filters.priority,
                onChange: (e) => setFilters({ ...filters, priority: e.target.value, currentPage: 1 }),
                options: [{ value: '', label: 'All Priorities' }, ...(filterOptions.priorities || [])],
                placeholder: 'All Priorities',
                width: '150px'
              }
            ]
          }}
          downloadButton={permissions.can_download ? {
            label: 'Download',
            onClick: handleDownload,
            variant: 'outline',
            className: 'border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500',
            disabled: loading || data.length === 0
          } : null}
        />
        <div className="mb-2 px-1">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-2 rounded">
            <p className="text-xs text-blue-800">
              <strong>SLA %</strong> = Tickets resolved within SLA target / Total resolved.
              <strong> Near Breach</strong> = Actual time between 80%-100% of SLA target (resolved within SLA but close to breaching).
              <strong> Avg Delay</strong> = Average overshoot time for breached tickets only.
            </p>
          </div>
        </div>
        <Table
          columns={columns}
          data={paginatedData}
          loading={loading}
          emptyMessage="No SLA data available for the selected filters"
        />
        {data.length > 0 && (
          <div className="px-4 py-3 bg-gray-50 border-t">
            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Total Tickets:</span>
                <span className="font-bold text-gray-900">{totals.totalTickets || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Total Resolved:</span>
                <span className="font-bold text-green-600">{totals.totalResolved || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Total Breached:</span>
                <span className="font-bold text-red-600">{totals.breached || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Overall SLA %:</span>
                <span className="font-bold" style={{ color: (totals.slaPct || 0) >= 80 ? '#059669' : (totals.slaPct || 0) >= 50 ? '#d97706' : '#dc2626' }}>
                  {totals.slaPct || 0}%
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Near Breach:</span>
                <span className="font-bold text-orange-600">{totals.nearBreachCount || 0}</span>
              </div>
            </div>
          </div>
        )}
        <Pagination
          currentPage={filters.currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={filters.itemsPerPage}
          onPageChange={(page) => setFilters({ ...filters, currentPage: page })}
          onItemsPerPageChange={(count) => setFilters({ ...filters, itemsPerPage: count, currentPage: 1 })}
        />
      </Card>
    </div>
  );
};

export default SlaReport;
